/**
 * Search Utilities
 * Provides smart search ranking and sorting where prefix matches (starts-with)
 * are prioritized over substring matches (contains).
 * Diacritics and accents (ê, é, è, ä, etc.) are stripped so e.g. "Creme" matches "Crême".
 * German digraphs (ae/oe/ue <-> ä/ö/ü) and 'ß' <-> 'ss' are symmetrically matched.
 */

/**
 * Normalizes text for search comparisons:
 * - Trims whitespace and converts to lowercase
 * - Strips diacritics / accents (ê -> e, é -> e, ä -> a, etc.) using Unicode NFD decomposition
 * - Replaces German 'ß' with 'ss'
 */
export function normalizeSearchText(text: string | undefined | null): string {
  if (!text || typeof text !== 'string') return ''
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
}

/**
 * Normalizes German digraph transliterations (ae -> a, oe -> o, ue -> u)
 * on top of normalized text to allow bidirectional matching of ä/ö/ü with ae/oe/ue.
 */
export function normalizeGermanDigraphs(text: string): string {
  return text
    .replace(/ae/g, 'a')
    .replace(/oe/g, 'o')
    .replace(/ue/g, 'u')
}

/**
 * Strips whitespace, hyphens, and punctuation separators from text to allow
 * matching compound words seamlessly (e.g. "Paprikagewürz" <-> "Paprika Gewürz" <-> "Paprika-Gewürz").
 */
export function stripSeparators(text: string | undefined | null): string {
  if (!text || typeof text !== 'string') return ''
  return text.replace(/[\s\-_/.,:;()\[\]"']+/g, '')
}

function calculateRank(s: string, q: string): number {
  if (s === q) return 0
  if (s.startsWith(q)) return 1

  // Match at beginning of words (e.g. "Dijon Senf", "Käse (Scheiben)", "Gouda (Käse)")
  const words = s.split(/[\s\-_\/,.:;()\[\]"']+/)
  if (words.some(w => w.startsWith(q))) return 2

  if (s.includes(q)) return 3

  // Compound / separator-agnostic match (e.g. "Paprikagewürz" <-> "Paprika Gewürz", "Paprika-Gewürz")
  const sClean = stripSeparators(s)
  const qClean = stripSeparators(q)
  if (sClean && qClean) {
    if (sClean === qClean) return 0
    if (sClean.startsWith(qClean)) return 1
    if (sClean.includes(qClean)) return 3
  }

  return Infinity
}

/**
 * Returns a search ranking score (lower is better):
 * - 0: Exact match
 * - 1: String starts with query (prefix match)
 * - 2: A word inside the string starts with query (word prefix match)
 * - 3: String contains query anywhere (substring match)
 * - Infinity: No match
 */
export function getSearchMatchRank(
  text: string | undefined | null,
  query: string,
  preNormalizedQuery?: string,
  preDigraphQuery?: string
): number {
  if (!text || typeof text !== 'string') return Infinity
  const q = preNormalizedQuery !== undefined ? preNormalizedQuery : normalizeSearchText(query)
  if (!q) return 0

  const s = normalizeSearchText(text)
  const baseRank = calculateRank(s, q)
  if (baseRank < Infinity) return baseRank

  // German digraph fallback (e.g. Kaese <-> Käse)
  const sDigraph = normalizeGermanDigraphs(s)
  const qDigraph = preDigraphQuery !== undefined ? preDigraphQuery : normalizeGermanDigraphs(q)
  if (sDigraph !== s || qDigraph !== q) {
    const digraphRank = calculateRank(sDigraph, qDigraph)
    if (digraphRank < Infinity) return digraphRank
  }

  return Infinity
}

/**
 * Returns a composite search ranking score where matches on the PRIMARY (real) name
 * are strictly prioritized over alternative names / aliases.
 *
 * Tier order (lower is better):
 * - 0..9: Primary exact match (e.g. "Käse")
 * - 10..19: Primary starts with query (e.g. "Käse (Scheiben)")
 * - 20..29: Primary word starts with query (e.g. "Gouda Käse")
 * - 30..39: Primary substring match (e.g. "Reibekäse")
 * - 40: Alias exact match (e.g. alias "Käse" on an article whose real name has no match)
 * - 50: Alias starts with query
 * - 60: Alias word starts with query
 * - 70: Alias substring match
 * - Infinity: No match
 */
export function getItemSearchMatchRank(
  primaryText: string | undefined | null,
  aliases: string[] | undefined | null,
  query: string
): number {
  const q = normalizeSearchText(query)
  if (!q) return 0
  const qDigraph = normalizeGermanDigraphs(q)

  const primaryRank = getSearchMatchRank(primaryText, q, q, qDigraph)

  let bestAliasRank = Infinity
  if (aliases && Array.isArray(aliases) && aliases.length > 0) {
    for (const alias of aliases) {
      const aRank = getSearchMatchRank(alias, q, q, qDigraph)
      if (aRank < bestAliasRank) {
        bestAliasRank = aRank
      }
    }
  }

  // If neither primary nor aliases match
  if (primaryRank === Infinity && bestAliasRank === Infinity) {
    return Infinity
  }

  // If primary (real name) matches:
  // Primary match ALWAYS comes before items that only matched via aliases!
  // Ranks 0..39
  if (primaryRank !== Infinity) {
    const aliasBonus = bestAliasRank !== Infinity ? Math.min(bestAliasRank, 8) : 9
    return primaryRank * 10 + aliasBonus
  }

  // Only aliases matched (ranks 40..79):
  return 40 + (bestAliasRank * 10)
}

/**
 * Compares two items based on search relevance against a query.
 * Real item name matches strictly precede alias matches.
 * Ties are broken by name length (closer to query) and German locale comparison.
 */
export function compareSearchRelevance<T = string>(
  itemA: T,
  itemB: T,
  query: string,
  getText: (item: T) => string = (item: any) => String(item || ''),
  getAliases?: (item: T) => string[] | undefined | null
): number {
  const q = query ? query.trim() : ''
  const textA = getText(itemA)
  const textB = getText(itemB)

  if (!q) {
    return textA.localeCompare(textB, 'de')
  }

  const rankA = getAliases
    ? getItemSearchMatchRank(textA, getAliases(itemA), q)
    : getSearchMatchRank(textA, q)
  const rankB = getAliases
    ? getItemSearchMatchRank(textB, getAliases(itemB), q)
    : getSearchMatchRank(textB, q)

  if (rankA !== rankB) {
    return rankA - rankB
  }

  // Tie-breaker 1: Shorter primary text (closer match)
  const normQ = normalizeSearchText(q)
  const normA = normalizeSearchText(textA)
  const normB = normalizeSearchText(textB)
  const lenDiff = Math.abs(normA.length - normQ.length) - Math.abs(normB.length - normQ.length)
  if (lenDiff !== 0) {
    return lenDiff
  }

  return textA.localeCompare(textB, 'de')
}

/**
 * Filters and sorts items with prefix matches first, word-prefix next, and contains last.
 * Supports checking optional aliases/alternative terms for each item.
 */
export function filterAndSortSearchResults<T>(
  items: T[],
  query: string,
  getText: (item: T) => string,
  getAliases?: (item: T) => string[] | undefined | null
): T[] {
  const q = query ? query.trim() : ''
  if (!q) {
    return [...items].sort((a, b) => getText(a).localeCompare(getText(b), 'de'))
  }

  return items
    .filter(item => {
      const rank = getAliases
        ? getItemSearchMatchRank(getText(item), getAliases(item), q)
        : getSearchMatchRank(getText(item), q)
      return rank < Infinity
    })
    .sort((a, b) => compareSearchRelevance(a, b, q, getText, getAliases))
}
